import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { WindowRef } from '@spartacus/core';
import { BREAKPOINT, LayoutBreakPoints, LayoutConfig } from '@spartacus/storefront';
import { Observable } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { DeviceTypes } from '../models';
import { UserAgentService } from './user-agent.service';

@Injectable({
  providedIn: 'root',
})
export class DeviceService {
  breakpoint$: Observable<BREAKPOINT> = isPlatformBrowser(this.platform)
    ? this.winRef.resize$.pipe(
        map((event) => this.getBreakpoint((<Window>event.target).innerWidth)),
        distinctUntilChanged(),
      )
    : this.userAgentService.breakpoint$;

  deviceType$ = this.breakpoint$.pipe(
    map((breakpoint) => {
      if ([BREAKPOINT.xxs, BREAKPOINT.xs, BREAKPOINT.sm].includes(breakpoint)) {
        return DeviceTypes.mobile;
      }

      if (breakpoint === BREAKPOINT.md) {
        return DeviceTypes.tablet;
      }

      return DeviceTypes.desktop;
    }),
    distinctUntilChanged(),
  );

  // XXS
  isMobileXxsDown$ = this.isDown(BREAKPOINT.xxs);
  isMobileXxsUp$ = this.isUp(BREAKPOINT.xxs);

  // XS
  isMobileXsDown$ = this.isDown(BREAKPOINT.xs);
  isMobileXsUp$ = this.isUp(BREAKPOINT.xs);

  // SM
  isMobileSmDown$ = this.isDown(BREAKPOINT.sm);
  isMobileSmUp$ = this.isUp(BREAKPOINT.sm);

  // MD
  isTabletMdDown$ = this.isDown(BREAKPOINT.md);
  isTabletMdUp$ = this.isUp(BREAKPOINT.md);

  // LG
  isDesktopLgDown$ = this.isDown(BREAKPOINT.lg);
  isDesktopLgUp$ = this.isUp(BREAKPOINT.lg);

  // XL
  isDesktopXlDown$ = this.isDown(BREAKPOINT.xl);
  isDesktopXlUp$ = this.isUp(BREAKPOINT.xl);

  // XXL
  isDesktopXxlDown$ = this.isDown(BREAKPOINT.xxl);
  isDesktopXxlUp$ = this.isUp(BREAKPOINT.xxl);

  sizes = {
    xxs: this.getSize(BREAKPOINT.xxs),
    xs: this.getSize(BREAKPOINT.xs),
    sm: this.getSize(BREAKPOINT.sm),
    md: this.getSize(BREAKPOINT.md),
    lg: this.getSize(BREAKPOINT.lg),
  };

  private _breakpoints: BREAKPOINT[];

  constructor(
    private winRef: WindowRef,
    private layoutConfig: LayoutConfig,
    @Inject(PLATFORM_ID) private platform: any,
    private userAgentService: UserAgentService,
  ) {}

  get breakpoints(): BREAKPOINT[] {
    if (!this._breakpoints) {
      this._breakpoints = this.resolveBreakpointsFromConfig();
    }

    return this._breakpoints;
  }

  isSdvorAppV2(): boolean {
    return this.userAgentService.userAgent.includes('SdvorAppV2');
  }

  isSdvorAppAndroid(): boolean {
    return this.userAgentService.userAgent.includes('SdvorApp/android');
  }

  isSdvorAppIos(): boolean {
    return this.userAgentService.userAgent.includes('SdvorApp/ios');
  }

  isSdvorApp(): boolean {
    return this.isSdvorAppIos() || this.isSdvorAppAndroid();
  }

  getAppClientId(): 'sd_webview_app' | 'sd_app' | null {
    if (this.isSdvorAppV2()) {
      return 'sd_app';
    }
    if (this.isSdvorApp()) {
      return 'sd_webview_app';
    }

    return null;
  }

  getSize(breakpoint: BREAKPOINT): number {
    return this.getEffectiveMaxSize(breakpoint);
  }

  isDown(breakpoint: BREAKPOINT): Observable<boolean> {
    return this.breakpoint$.pipe(
      map((br) => this.breakpoints.slice(0, this.breakpoints.indexOf(breakpoint) + 1).includes(br)),
    );
  }

  isUp(breakpoint: BREAKPOINT): Observable<boolean> {
    return this.breakpoint$.pipe(
      map((br) => this.breakpoints.slice(this.breakpoints.indexOf(breakpoint)).includes(br)),
    );
  }

  isEqual(breakpoint: BREAKPOINT): Observable<boolean> {
    return this.breakpoint$.pipe(map((br) => br === breakpoint));
  }

  protected get fallbackBreakpoint(): BREAKPOINT {
    return this.breakpoints?.[0];
  }

  protected resolveBreakpointsFromConfig(): BREAKPOINT[] {
    /** Проблема:
     * Object.keys() возвращал брейкпоинты в порядке:
     *  ["xs", "sm", "md", "lg", "xl", "xxl", "xxs"] (xxs в конце!)
     *  Поэтому явно задаем верный порядок
     */
    const predefinedOrder: BREAKPOINT[] = [
      BREAKPOINT.xxs,
      BREAKPOINT.xs,
      BREAKPOINT.sm,
      BREAKPOINT.md,
      BREAKPOINT.lg,
      BREAKPOINT.xl,
      BREAKPOINT.xxl,
    ];

    // Фильтруем только те брейкпоинты, которые есть в конфиге
    return predefinedOrder.filter((breakpoint) => this.config[breakpoint] !== undefined);
  }

  protected getMaxSize(breakpoint: BREAKPOINT): number {
    const breakpointConfig = this.config[breakpoint];

    if (!breakpointConfig) {
      return 0;
    }

    if (typeof breakpointConfig === 'number') {
      // Для xxs: 0 вычисляем max = min следующего - 1
      const index = this.breakpoints.indexOf(breakpoint);
      const next = this.breakpoints[index + 1];

      return next ? this.getMinSize(next) - 1 : 0;
    } else if (breakpointConfig.max) {
      return breakpointConfig.max;
    } else {
      // Если нет max, вычисляем из следующего min
      const index = this.breakpoints.indexOf(breakpoint);
      const next = this.breakpoints[index + 1];

      return next ? this.getMinSize(next) - 1 : 0;
    }
  }

  protected getMinSize(breakpoint: BREAKPOINT): number {
    const config = this.config[breakpoint];

    if (typeof config === 'number') {
      return config; // Для xxs = 0
    }

    return config?.min || 0;
  }

  protected getBreakpoint(windowWidth: number): BREAKPOINT {
    for (let i = 0; i < this.breakpoints.length; i++) {
      const breakpoint = this.breakpoints[i];
      const maxSize = this.getMaxSize(breakpoint);

      if ((maxSize > 0 && windowWidth < maxSize) || i === this.breakpoints.length - 1) {
        return breakpoint;
      }
    }

    return BREAKPOINT.xl; // TODO: оставил тут xl т.к. после 1280 нет изменений
  }

  private getEffectiveMaxSize(breakpoint: BREAKPOINT): number {
    const maxSize = this.getMaxSize(breakpoint);

    if (maxSize > 0) {
      return maxSize;
    }

    // Для того чтобы обработать max брейкпоинт, в нашем случае xl
    return 99999;
  }

  protected get config(): LayoutBreakPoints {
    return this.layoutConfig?.breakpoints || {};
  }
}
